import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Grid, Chip,
  Alert, CircularProgress, MenuItem, Checkbox, Tooltip,
  FormControlLabel, Switch, Divider, IconButton, Badge
} from '@mui/material';
import LabelIcon from '@mui/icons-material/Label';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FilterListIcon from '@mui/icons-material/FilterList';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import DeselectIcon from '@mui/icons-material/Deselect';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';

const COLOR_OPE = '#1565c0';
const COLOR_INV = '#2e7d32';
const COLOR_FIN = '#6a1b9a';
const TIPO_COLORS = { OPE: COLOR_OPE, INV: COLOR_INV, FIN: COLOR_FIN };

const fmt = v => {
  const n = parseFloat(v || 0);
  const abs = Math.abs(n).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `(${abs})` : abs;
};

export default function UrcClasificacion() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [fechaIni, setFechaIni] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [soloCaja, setSoloCaja] = useState(true);
  const [lineas, setLineas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [urcs, setUrcs] = useState([]);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [urcMasivo, setUrcMasivo] = useState('');
  const [filtroUrc, setFiltroUrc] = useState('');

  useEffect(() => {
    const now = new Date();
    const anio = now.getFullYear();
    setFechaIni(`${anio}-01-01`);
    setFechaFin(`${anio}-12-31`);

    axios.get('/api/contabilidad/empresas')
      .then(r => {
        const todas = Array.isArray(r.data) ? r.data : [];
        setEmpresas(todas);
        const activas = todas.filter(e => e.activo);
        if (activas.length === 1) setEmpresaId(String(activas[0].id));
      })
      .catch(() => {});

    axios.get('/api/contabilidad/urc')
      .then(r => setUrcs(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    setError('');
    setSeleccionados(new Set());
    try {
      const r = await axios.get('/api/contabilidad/urc-pendientes', {
        params: {
          empresa_id: empresaId,
          fecha_ini: fechaIni,
          fecha_fin: fechaFin,
          solo_caja: soloCaja ? '1' : '0'
        }
      });
      setLineas(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [empresaId, fechaIni, fechaFin, soloCaja]);

  const toggleSeleccion = (id) => {
    setSeleccionados(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const selectAll = () => setSeleccionados(new Set(lineasFiltradas.map(l => l.movdet_id)));
  const deselectAll = () => setSeleccionados(new Set());

  const aplicarUrcMasivo = () => {
    if (!urcMasivo || seleccionados.size === 0) return;
    setLineas(prev => prev.map(l =>
      seleccionados.has(l.movdet_id) ? { ...l, urc_codigo_pendiente: urcMasivo } : l
    ));
    setSuccess(`URC ${urcMasivo} asignado a ${seleccionados.size} línea(s). Haga clic en "Guardar cambios" para confirmar.`);
    setTimeout(() => setSuccess(''), 5000);
  };

  const cambiarUrcLinea = (movdet_id, urc) => {
    setLineas(prev => prev.map(l =>
      l.movdet_id === movdet_id ? { ...l, urc_codigo_pendiente: urc } : l
    ));
  };

  const guardar = async () => {
    const conCambios = lineas.filter(l => l.urc_codigo_pendiente);
    if (conCambios.length === 0) { setError('No hay cambios pendientes por guardar'); return; }
    setSaving(true);
    setError('');
    try {
      const asignaciones = conCambios.map(l => ({
        movdet_id: l.movdet_id,
        urc_codigo: l.urc_codigo_pendiente
      }));
      await axios.put('/api/contabilidad/urc-masivo', { asignaciones });
      setSuccess(`${conCambios.length} línea(s) clasificadas correctamente.`);
      setTimeout(() => setSuccess(''), 4000);
      await cargar();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const lineasFiltradas = lineas;
  const pendientesGuardar = lineas.filter(l => l.urc_codigo_pendiente).length;

  const urcInfo = (codigo) => urcs.find(u => u.codigo === codigo);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {/* Encabezado */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <LabelIcon sx={{ fontSize: 32, color: '#6a1b9a' }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">Clasificación URC — Retroactiva</Typography>
            <Typography variant="body2" color="text.secondary">
              Asigne códigos URC en lote a líneas de asiento sin clasificar
            </Typography>
          </Box>
        </Box>
        <TextField select label="Empresa" size="small" value={empresaId}
          onChange={e => { setEmpresaId(e.target.value); setLineas([]); }}
          sx={{ minWidth: 260, ml: { sm: 'auto' } }}>
          {empresas.map(e => (
            <MenuItem key={e.id} value={String(e.id)}>
              {e.razon_social}{!e.activo && <span style={{ color: '#999', marginLeft: 6 }}>(Inactiva)</span>}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Filtros */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField label="Fecha inicio" type="date" size="small" fullWidth
              value={fechaIni} InputLabelProps={{ shrink: true }}
              onChange={e => setFechaIni(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField label="Fecha fin" type="date" size="small" fullWidth
              value={fechaFin} InputLabelProps={{ shrink: true }}
              onChange={e => setFechaFin(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControlLabel
              control={<Switch checked={soloCaja} onChange={e => setSoloCaja(e.target.checked)} size="small" />}
              label={<Typography variant="body2">Solo cuentas de caja (10/12)</Typography>}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button fullWidth variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <FilterListIcon />}
              onClick={cargar} disabled={loading || !empresaId}>
              {loading ? 'Buscando...' : 'Buscar sin clasificar'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>{success}</Alert>}

      {lineas.length === 0 && !loading && (
        <Alert severity="info">
          {empresaId
            ? 'No hay líneas sin clasificar en el período seleccionado. ¡Excelente!'
            : 'Seleccione empresa y haga clic en "Buscar sin clasificar".'}
        </Alert>
      )}

      {lineas.length > 0 && (
        <>
          {/* Barra de acción masiva */}
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#f3e5f5' }}>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item>
                <Tooltip title="Seleccionar todos los visibles">
                  <IconButton size="small" onClick={selectAll}><SelectAllIcon /></IconButton>
                </Tooltip>
                <Tooltip title="Deseleccionar todos">
                  <IconButton size="small" onClick={deselectAll}><DeselectIcon /></IconButton>
                </Tooltip>
              </Grid>
              <Grid item>
                <Chip label={`${seleccionados.size} seleccionados`} size="small"
                  color={seleccionados.size > 0 ? 'secondary' : 'default'} />
              </Grid>
              <Grid item xs>
                <TextField select size="small" label="Asignar URC a seleccionados"
                  value={urcMasivo} onChange={e => setUrcMasivo(e.target.value)}
                  disabled={seleccionados.size === 0}
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 280 }}>
                  <MenuItem value=""><em>Elegir código URC...</em></MenuItem>
                  {['OPE', 'INV', 'FIN'].map(tipo => [
                    <MenuItem key={`hdr-${tipo}`} disabled sx={{ fontSize: 11, fontWeight: 'bold', color: TIPO_COLORS[tipo] }}>
                      — {tipo} —
                    </MenuItem>,
                    ...urcs.filter(u => u.tipo_actividad === tipo).map(u => (
                      <MenuItem key={u.codigo} value={u.codigo}>
                        <Chip label={u.codigo} size="small" sx={{ mr: 1, fontSize: 10, height: 18, bgcolor: `${TIPO_COLORS[tipo]}22`, color: TIPO_COLORS[tipo] }} />
                        {u.descripcion}
                      </MenuItem>
                    ))
                  ])}
                </TextField>
              </Grid>
              <Grid item>
                <Button variant="contained" color="secondary" size="small"
                  onClick={aplicarUrcMasivo} disabled={!urcMasivo || seleccionados.size === 0}>
                  Aplicar a seleccionados
                </Button>
              </Grid>
              <Grid item>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              </Grid>
              <Grid item>
                <Badge badgeContent={pendientesGuardar} color="warning">
                  <Button variant="contained" color="success" size="small"
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                    onClick={guardar} disabled={saving || pendientesGuardar === 0}>
                    {saving ? 'Guardando...' : 'Guardar cambios'}
                  </Button>
                </Badge>
              </Grid>
              <Grid item>
                <IconButton size="small" onClick={cargar} title="Recargar">
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          </Paper>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {lineas.length} línea(s) sin URC encontradas
            {pendientesGuardar > 0 && ` • ${pendientesGuardar} con cambios pendientes de guardar`}
          </Typography>

          <Paper elevation={2} sx={{ overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 580 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ width: 40 }} />
                    <TableCell sx={{ fontWeight: 'bold', fontSize: 11, width: 90 }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: 11 }}>Asiento</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: 11 }}>Cuenta</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: 11 }}>Glosa línea</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: 11, width: 90 }}>Debe</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: 11, width: 90 }}>Haber</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: 11, minWidth: 260 }}>Código URC</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineas.map((l) => {
                    const sel = seleccionados.has(l.movdet_id);
                    const pendiente = l.urc_codigo_pendiente;
                    const urcDefault = l.urc_codigo_default;
                    const infoUrc = pendiente ? urcInfo(pendiente) : null;
                    const tipoColor = infoUrc ? TIPO_COLORS[infoUrc.tipo_actividad] : null;

                    return (
                      <TableRow key={l.movdet_id}
                        sx={{
                          bgcolor: pendiente ? `${tipoColor}11` : sel ? '#e8eaf6' : 'inherit',
                          '&:hover': { bgcolor: pendiente ? `${tipoColor}22` : '#f3e5f5' }
                        }}>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={sel} onChange={() => toggleSeleccion(l.movdet_id)} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 11, fontFamily: 'monospace' }}>
                          {l.fecha?.slice(0, 10)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11 }}>
                          <Typography variant="caption" fontFamily="monospace" color="primary">
                            {l.tipo_documento}-{String(l.numero).padStart(6, '0')}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary"
                            sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {l.glosa}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: 11 }}>
                          <Typography variant="caption" fontFamily="monospace" fontWeight="bold">
                            {l.cuenta_codigo}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary"
                            sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {l.cuenta_descripcion}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: 11, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {l.glosa_det || '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 11, fontFamily: 'monospace', color: '#1565c0' }}>
                          {parseFloat(l.debe_mn || 0) > 0 ? fmt(l.debe_mn) : ''}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 11, fontFamily: 'monospace', color: '#c62828' }}>
                          {parseFloat(l.haber_mn || 0) > 0 ? fmt(l.haber_mn) : ''}
                        </TableCell>
                        <TableCell>
                          <TextField select size="small" fullWidth
                            value={pendiente || ''}
                            onChange={e => cambiarUrcLinea(l.movdet_id, e.target.value)}
                            SelectProps={{ displayEmpty: true }}
                            sx={{
                              '& .MuiInputBase-root': {
                                bgcolor: pendiente ? `${tipoColor}18` : 'transparent',
                                fontSize: 11
                              }
                            }}>
                            <MenuItem value="">
                              {urcDefault
                                ? <em style={{ color: '#888' }}>Sugerido: {urcDefault} — {urcInfo(urcDefault)?.descripcion}</em>
                                : <em>Sin URC — seleccionar...</em>}
                            </MenuItem>
                            {['OPE', 'INV', 'FIN'].map(tipo => [
                              <MenuItem key={`hdr-${tipo}`} disabled sx={{ fontSize: 10, color: TIPO_COLORS[tipo] }}>
                                — {tipo} —
                              </MenuItem>,
                              ...urcs.filter(u => u.tipo_actividad === tipo).map(u => (
                                <MenuItem key={u.codigo} value={u.codigo} sx={{ fontSize: 11 }}>
                                  <Chip label={u.codigo} size="small"
                                    sx={{ mr: 0.5, fontSize: 9, height: 16, bgcolor: `${TIPO_COLORS[tipo]}22`, color: TIPO_COLORS[tipo] }} />
                                  {u.descripcion}
                                </MenuItem>
                              ))
                            ])}
                          </TextField>
                          {urcDefault && !pendiente && (
                            <Tooltip title={`Aplicar URC por defecto de la cuenta: ${urcDefault}`}>
                              <Button size="small" variant="text" sx={{ fontSize: 9, mt: 0.3, p: '1px 4px', minWidth: 0 }}
                                onClick={() => cambiarUrcLinea(l.movdet_id, urcDefault)}>
                                Aplicar default
                              </Button>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
}
